import { BAR_FILL_FADE_FROM } from '@alison/ui';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { type ReactNode, useId } from 'react';
import { deltaPercentPoints } from '../../../../lib/format.js';
import { Delta } from '../../../media/shared/Delta.js';
import { scoreTier } from '../../data/creativeScore.js';

const COMPACT_BELOW = 80;

interface ScoreGaugeProps {
  score: number | null;
  previous: number | null;
  size: number;
  deltaVariant?: 'arrow' | 'chip';
}

export function scoreDelta(score: number | null, previous: number | null): number | null {
  if (score === null || previous === null || previous === 0) return null;
  return (score - previous) / previous;
}

export function ScoreGauge({
  score,
  previous,
  size,
  deltaVariant = 'arrow',
}: ScoreGaugeProps): ReactNode {
  const theme = useTheme();
  const fadeId = useId();
  const tier = score === null ? null : scoreTier(score);
  const delta = scoreDelta(score, previous);
  const compact = size < COMPACT_BELOW;
  const pillPadding = compact
    ? { px: 0, py: 0 }
    : size < 120
      ? { px: 1.5, py: 0.5 }
      : { px: 2, py: 1 };
  const deltaIconSize = compact ? Math.round(size / 5) : 14;
  const deltaFontSize = compact ? 9 : 10;

  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <CircularProgress
        variant="determinate"
        value={100}
        thickness={3}
        size={size}
        sx={{ position: 'absolute', color: 'divider' }}
      />
      {tier !== null && (
        <>
          <svg width={0} height={0} aria-hidden="true" style={{ position: 'absolute' }}>
            <defs>
              <linearGradient id={fadeId} x1="0" y1="0" x2="1" y2="0">
                <stop
                  offset="0%"
                  stopColor={alpha(theme.palette.score[tier].main, BAR_FILL_FADE_FROM)}
                />
                <stop offset="100%" stopColor={theme.palette.score[tier].main} />
              </linearGradient>
            </defs>
          </svg>
          <CircularProgress
            variant="determinate"
            value={score ?? 0}
            thickness={3}
            size={size}
            sx={{
              position: 'absolute',
              color: theme.palette.score[tier].main,
              '& .MuiCircularProgress-circle': { stroke: `url(#${fadeId})` },
            }}
          />
        </>
      )}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: compact ? 0 : 1,
        }}
      >
        <Typography sx={{ fontSize: Math.round(size / 5), fontWeight: 400, color: 'text.primary' }}>
          {score === null ? '—' : Math.round(score)}
        </Typography>
        {delta !== null && deltaVariant === 'chip' && (
          <Delta value={deltaPercentPoints(delta) ?? 0} />
        )}
        {delta !== null && deltaVariant === 'arrow' && (
          <Box
            sx={(theme) => {
              const color =
                delta < 0 ? theme.palette.score.low.main : theme.palette.score.high.main;
              return {
                display: 'flex',
                alignItems: 'center',
                gap: compact ? 0 : 0.5,
                ...pillPadding,
                borderRadius: `${theme.radius.md}px`,
                bgcolor: compact ? 'transparent' : alpha(color, 0.1),
                color,
              };
            }}
          >
            {delta < 0 ? (
              <ArrowDownward sx={{ fontSize: deltaIconSize }} />
            ) : (
              <ArrowUpward sx={{ fontSize: deltaIconSize }} />
            )}
            <Typography
              sx={{
                fontSize: deltaFontSize,
                fontWeight: 700,
                textTransform: 'uppercase',
                color: 'inherit',
              }}
            >
              {Math.abs(Math.round(delta * 100))}%
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
